import { useState } from 'react';
import axios from 'axios';
import { FileSignature, CheckCircle, Clock, Send, Save, Edit, Trash2, Loader2, Download, Eye, Shield } from 'lucide-react';
import { toast } from 'sonner';

const ConventionStageSection = ({ stagiaireId, stagiaire, convStage, setConvStage, readOnly }) => {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    date_debut_formation: '', date_fin_formation: '',
    periodes_stage: [{ debut: '', fin: '' }],
    lieu_signature: ''
  });
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.post(`/convention-stage/${stagiaireId}`, form);
      toast.success('Convention enregistrée');
      const res = await axios.get(`/convention-stage/${stagiaireId}`);
      if (res.data) setConvStage(res.data);
      setShowForm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setSaving(false);
    }
  };

  const handleSend = async () => {
    if (!window.confirm("Envoyer la convention pour signature ?")) return;
    try {
      await axios.post(`/convention-stage/${stagiaireId}/send`);
      toast.success('Convention envoyée pour signature');
      const res = await axios.get(`/convention-stage/${stagiaireId}`);
      if (res.data) setConvStage(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleValidate = async () => {
    if (!window.confirm("Valider et archiver la convention signée ?")) return;
    try {
      await axios.post(`/convention-stage/${stagiaireId}/validate`);
      toast.success('Convention validée et archivée');
      const res = await axios.get(`/convention-stage/${stagiaireId}`);
      if (res.data) setConvStage(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm("Supprimer cette convention ?")) return;
    try {
      await axios.delete(`/convention-stage/${stagiaireId}`);
      toast.success('Convention supprimée');
      setConvStage({});
      setShowForm(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
  };

  const viewPdf = async () => {
    try {
      const res = await axios.get(`/convention-stage/${stagiaireId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error('Impossible de charger le PDF');
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await axios.get(`/convention-stage/${stagiaireId}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stagiaire?.user_info?.nom || ''}_${stagiaire?.user_info?.prenom || ''}_convention_stage.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error('Erreur téléchargement PDF');
    }
  };

  const handleArchiveConvention = async () => {
    try {
      await axios.post(`/convention-stage/${stagiaireId}/archive`);
      toast.success('Convention de Mise en Situation Pédagogique archivée avec succès');
      const res = await axios.get(`/convention-stage/${stagiaireId}`);
      if (res.data) setConvStage(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'archivage");
    }
  };

  const handleViewArchive = async () => {
    try {
      const response = await axios.get(`/convention-stage/${stagiaireId}/archives/convention_stage/view`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (e) {
      toast.error("Erreur lors de l'ouverture de l'archive");
    }
  };

  const handleDownloadArchive = async () => {
    try {
      const response = await axios.get(`/convention-stage/${stagiaireId}/archives/convention_stage/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stagiaire?.user_info?.nom || ''}_${stagiaire?.user_info?.prenom || ''}_convention_stage_archive.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Erreur lors du téléchargement de l'archive");
    }
  };

  const openEditForm = () => {
    setForm({
      date_debut_formation: convStage.date_debut_formation || '',
      date_fin_formation: convStage.date_fin_formation || '',
      periodes_stage: convStage.periodes_stage?.length ? convStage.periodes_stage : [{ debut: '', fin: '' }],
      lieu_signature: convStage.lieu_signature || ''
    });
    setShowForm(true);
  };

  if (!(stagiaire.entreprise_id || stagiaire.tuteur_id)) return null;

  const statusBadge = () => {
    if (convStage.archive_date) return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Archivée</span>;
    if (convStage.statut === 'signe') return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">Signée</span>;
    if (convStage.statut === 'en_attente_validation_centre') return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium animate-pulse">À valider</span>;
    if (convStage.statut === 'partiellement_signe' || convStage.statut === 'en_attente_signatures') return <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium">Envoyée</span>;
    if (convStage.statut === 'brouillon') return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">Créée</span>;
    return <span className="px-3 py-1 bg-slate-100 text-slate-500 rounded-full text-sm font-medium">Non créée</span>;
  };

  const hasConvention = !!convStage.statut;
  const isBrouillon = convStage.statut === 'brouillon';
  const isSent = ['en_attente_signatures', 'partiellement_signe'].includes(convStage.statut);
  const isWaitingValidation = convStage.statut === 'en_attente_validation_centre';
  const isSigned = convStage.statut === 'signe';
  const isArchived = !!convStage.archive_date;

  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 shadow-sm" data-testid="convention-stage-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-2">
        <h2 className="text-xl font-display font-semibold text-blue-800 flex items-center gap-2">
          <FileSignature className="h-5 w-5 text-blue-600" />
          Convention de Mise en Situation Pédagogique
        </h2>
        {statusBadge()}
      </div>

      {/* Bandeau archive */}
      {isArchived && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg mb-4" data-testid="convention-stage-archive-info">
          <Shield className="h-5 w-5 text-green-600 shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-800">PDF archivé</p>
            <p className="text-xs text-green-600">
              Archivé le {new Date(convStage.archive_date).toLocaleDateString('fr-FR')} par {convStage.archive_par}
            </p>
          </div>
          <div className="flex gap-1">
            <button onClick={handleViewArchive} className="p-1.5 text-green-700 hover:bg-green-100 rounded" data-testid="convention-stage-archive-view" title="Prévisualiser"><Eye className="h-4 w-4" /></button>
            <button onClick={handleDownloadArchive} className="p-1.5 text-green-700 hover:bg-green-100 rounded" data-testid="convention-stage-archive-download" title="Télécharger"><Download className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {/* Formulaire de création/modification */}
      {showForm && (
        <div className="bg-white border border-blue-200 rounded-lg p-4 mb-4 space-y-4">
          <h3 className="font-semibold text-blue-700 text-sm">Variables de la convention</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-xs text-slate-500">Début formation</label>
              <input type="date" value={form.date_debut_formation}
                onChange={e => setForm({...form, date_debut_formation: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Fin formation</label>
              <input type="date" value={form.date_fin_formation}
                onChange={e => setForm({...form, date_fin_formation: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm" />
            </div>
            <div>
              <label className="text-xs text-slate-500">Lieu de signature</label>
              <input type="text" value={form.lieu_signature}
                onChange={e => setForm({...form, lieu_signature: e.target.value})}
                className="w-full px-3 py-2 border rounded-lg text-sm" placeholder="Ex: La Rochelle" />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs text-slate-500 font-medium">Périodes de stage en entreprise</label>
              <button type="button" onClick={() => setForm({...form, periodes_stage: [...form.periodes_stage, { debut: '', fin: '' }]})} className="text-xs text-blue-600 hover:text-blue-800">+ Ajouter une période</button>
            </div>
            <div className="space-y-2">
              {form.periodes_stage.map((periode, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-xs text-slate-400 w-6">#{idx + 1}</span>
                  <input type="date" value={periode.debut}
                    onChange={e => { const updated = [...form.periodes_stage]; updated[idx].debut = e.target.value; setForm({...form, periodes_stage: updated}); }}
                    className="flex-1 px-3 py-1.5 border rounded text-sm" />
                  <span className="text-xs text-slate-400">au</span>
                  <input type="date" value={periode.fin}
                    onChange={e => { const updated = [...form.periodes_stage]; updated[idx].fin = e.target.value; setForm({...form, periodes_stage: updated}); }}
                    className="flex-1 px-3 py-1.5 border rounded text-sm" />
                  {form.periodes_stage.length > 1 && (
                    <button onClick={() => setForm({...form, periodes_stage: form.periodes_stage.filter((_, i) => i !== idx)})} className="text-red-400 hover:text-red-600 text-xs p-1">x</button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="flex gap-2 pt-2">
            <button onClick={handleSave} disabled={saving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-1 disabled:opacity-50" data-testid="save-convention-btn">
              <Save className="h-4 w-4" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg text-sm hover:bg-slate-300">Annuler</button>
          </div>
        </div>
      )}

      {/* Convention créée : affichage des données + actions */}
      {hasConvention && !showForm && (
        <div className="space-y-4">
          {/* Résumé des dates */}
          <div className="bg-white border border-blue-200 rounded-lg p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500">Formation : </span>
                <span className="font-medium">
                  {convStage.date_debut_formation ? new Date(convStage.date_debut_formation).toLocaleDateString('fr-FR') : '-'}
                  {' au '}
                  {convStage.date_fin_formation ? new Date(convStage.date_fin_formation).toLocaleDateString('fr-FR') : '-'}
                </span>
              </div>
              {convStage.lieu_signature && (
                <div>
                  <span className="text-slate-500">Lieu : </span>
                  <span className="font-medium">{convStage.lieu_signature}</span>
                </div>
              )}
            </div>
            {convStage.periodes_stage?.length > 0 && (
              <div className="mt-3">
                <span className="text-xs text-slate-500 font-medium">Périodes de stage :</span>
                <div className="mt-1 space-y-1">
                  {convStage.periodes_stage.map((p, idx) => (
                    <div key={idx} className="text-sm bg-blue-50 px-2 py-1 rounded inline-block mr-2">
                      {p.debut ? new Date(p.debut).toLocaleDateString('fr-FR') : '?'} au {p.fin ? new Date(p.fin).toLocaleDateString('fr-FR') : '?'}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Signatures (visible quand envoyée) */}
          {(isSent || isWaitingValidation || isSigned) && (
            <div className="bg-white border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-medium text-blue-700 mb-2">Signatures</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {['centre', 'stagiaire', 'tuteur', 'entreprise'].map(role => {
                  const signed = !!(convStage[`signature_${role}`] || convStage[`signature_${role}_present`]);
                  const labels = { centre: 'Centre FISL', stagiaire: 'Stagiaire', tuteur: 'Tuteur', entreprise: 'Entreprise' };
                  return (
                    <div key={role} className={`p-2 rounded text-center ${signed ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-slate-50 text-slate-400 border border-slate-200'}`} data-testid={`conv-stage-sig-${role}`}>
                      {signed ? <CheckCircle className="h-4 w-4 mx-auto mb-1" /> : <Clock className="h-4 w-4 mx-auto mb-1" />}
                      <span>{labels[role]}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Boutons d'actions */}
          {!readOnly && (
            <div className="flex flex-wrap gap-2">
              <button onClick={viewPdf} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 flex items-center gap-1" data-testid="view-convention-pdf">
                <Eye className="h-4 w-4" /> Voir le PDF
              </button>
              <button onClick={downloadPdf} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 flex items-center gap-1" data-testid="download-convention-pdf">
                <Download className="h-4 w-4" /> Télécharger
              </button>
              {isBrouillon && (
                <button onClick={openEditForm} className="px-3 py-2 bg-blue-100 text-blue-700 rounded-lg text-sm hover:bg-blue-200 flex items-center gap-1" data-testid="edit-convention-btn">
                  <Edit className="h-4 w-4" /> Modifier
                </button>
              )}
              {isBrouillon && (
                <button onClick={handleSend} className="px-3 py-2 bg-amber-100 text-amber-700 rounded-lg text-sm hover:bg-amber-200 flex items-center gap-1" data-testid="send-convention-btn">
                  <Send className="h-4 w-4" /> Envoyer pour signature
                </button>
              )}
              {isWaitingValidation && (
                <button onClick={handleValidate} className="px-3 py-2 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 flex items-center gap-1" data-testid="validate-convention-btn">
                  <CheckCircle className="h-4 w-4" /> Valider et archiver
                </button>
              )}
              {!isArchived && (
                <button onClick={handleArchiveConvention} className="px-3 py-2 bg-green-50 text-green-700 border border-green-300 rounded-lg text-sm hover:bg-green-100 flex items-center gap-1" data-testid="btn-archive-convention-stage">
                  <Shield className="h-4 w-4" /> Archiver
                </button>
              )}
              <button onClick={handleDelete} className="px-3 py-2 bg-red-100 text-red-700 rounded-lg text-sm hover:bg-red-200 flex items-center gap-1" data-testid="delete-convention-btn">
                <Trash2 className="h-4 w-4" /> Supprimer
              </button>
            </div>
          )}
        </div>
      )}

      {/* État initial : pas de convention */}
      {!hasConvention && !showForm && !readOnly && (
        <div className="text-center py-4">
          <p className="text-sm text-slate-500 mb-3">Aucune convention de mise en situation pédagogique créée.</p>
          <button
            onClick={() => setShowForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 flex items-center gap-2 mx-auto"
            data-testid="create-convention-btn"
          >
            <FileSignature className="h-4 w-4" />
            Créer
          </button>
        </div>
      )}
    </div>
  );
};

export default ConventionStageSection;
